import React from 'react';
import { Settings, FileText, Bell, Lock } from 'lucide-react';
import { toast, Toaster } from 'sonner';

export default function AdminSettingsPage() {
  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <Toaster position="top-center" theme="dark" />

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>Settings</h1>
        <p className="text-zinc-500 text-sm mt-1">Configure system settings and preferences</p>
      </div>

      {/* Settings sections */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Report Settings */}
        <div className="bg-zinc-900/50 rounded-2xl border border-zinc-800 overflow-hidden">
          <div className="p-6 border-b border-zinc-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center">
                <FileText className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Report Configuration</h3>
                <p className="text-zinc-500 text-sm">Letterhead and report settings</p>
              </div>
            </div>
          </div>
          
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-zinc-400 text-sm mb-2">Company Name</label>
              <input
                type="text"
                defaultValue="NaoView"
                className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-zinc-400 text-sm mb-2">Company Tagline</label>
              <input
                type="text"
                defaultValue="Stream & Earn Platform"
                className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-zinc-400 text-sm mb-2">Company Address</label>
              <textarea
                defaultValue="123 Business Street, Tech City"
                className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-indigo-500 resize-none"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-zinc-400 text-sm mb-2">Phone</label>
                <input
                  type="text"
                  defaultValue="+254 700 000 000"
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-zinc-400 text-sm mb-2">Email</label>
                <input
                  type="email"
                  defaultValue="admin@naoview.app"
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-zinc-400 text-sm mb-2">Report Footer Text</label>
              <textarea
                defaultValue="This is a system-generated report. For queries, contact support@naoview.app"
                className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-indigo-500 resize-none"
                rows={2}
              />
            </div>
            <button
              onClick={() => toast.success('Settings saved successfully')}
              className="w-full py-3 bg-indigo-500 text-white font-bold rounded-xl hover:bg-indigo-600 transition-all"
            >
              Save Report Settings
            </button>
          </div>
        </div>

        {/* Platform Settings */}
        <div className="bg-zinc-900/50 rounded-2xl border border-zinc-800 overflow-hidden">
          <div className="p-6 border-b border-zinc-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Settings className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Platform Settings</h3>
                <p className="text-zinc-500 text-sm">General platform configuration</p>
              </div>
            </div>
          </div>
          
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-zinc-400 text-sm mb-2">Platform Fee (%)</label>
              <input
                type="number"
                defaultValue={40}
                className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
              />
              <p className="text-zinc-600 text-xs mt-1">Percentage taken from all transactions</p>
            </div>
            <div>
              <label className="block text-zinc-400 text-sm mb-2">Free Viewing Time (seconds)</label>
              <input
                type="number"
                defaultValue={60}
                className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-zinc-400 text-sm mb-2">Minimum Stream Price (tokens)</label>
              <input
                type="number"
                defaultValue={0.1}
                step={0.1}
                className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-zinc-400 text-sm mb-2">Maximum Stream Price (tokens)</label>
              <input
                type="number"
                defaultValue={1000}
                className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
              />
            </div>
            <button
              onClick={() => toast.success('Settings saved successfully')}
              className="w-full py-3 bg-amber-500 text-white font-bold rounded-xl hover:bg-amber-600 transition-all"
            >
              Save Platform Settings
            </button>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="bg-zinc-900/50 rounded-2xl border border-zinc-800 overflow-hidden">
          <div className="p-6 border-b border-zinc-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <Bell className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Notifications</h3>
                <p className="text-zinc-500 text-sm">Configure alert preferences</p>
              </div>
            </div>
          </div>
          
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium">Email Alerts</p>
                <p className="text-zinc-500 text-sm">Receive email for important events</p>
              </div>
              <button className="w-12 h-6 bg-indigo-500 rounded-full relative">
                <span className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full" />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium">New User Alerts</p>
                <p className="text-zinc-500 text-sm">Notification when new users register</p>
              </div>
              <button className="w-12 h-6 bg-zinc-700 rounded-full relative">
                <span className="absolute left-1 top-1 w-4 h-4 bg-zinc-500 rounded-full" />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium">Stream Activity</p>
                <p className="text-zinc-500 text-sm">Alerts for stream starts/ends</p>
              </div>
              <button className="w-12 h-6 bg-indigo-500 rounded-full relative">
                <span className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full" />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium">Revenue Milestones</p>
                <p className="text-zinc-500 text-sm">Alert when revenue goals are hit</p>
              </div>
              <button className="w-12 h-6 bg-indigo-500 rounded-full relative">
                <span className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full" />
              </button>
            </div>
          </div>
        </div>

        {/* Security Settings */}
        <div className="bg-zinc-900/50 rounded-2xl border border-zinc-800 overflow-hidden">
          <div className="p-6 border-b border-zinc-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
                <Lock className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Security</h3>
                <p className="text-zinc-500 text-sm">Account security settings</p>
              </div>
            </div>
          </div>
          
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-zinc-400 text-sm mb-2">Current Password</label>
              <input
                type="password"
                placeholder="••••••••"
                className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-zinc-400 text-sm mb-2">New Password</label>
              <input
                type="password"
                placeholder="••••••••"
                className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-zinc-400 text-sm mb-2">Confirm New Password</label>
              <input
                type="password"
                placeholder="••••••••"
                className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
              />
            </div>
            <button
              onClick={() => toast.success('Password changed successfully')}
              className="w-full py-3 bg-red-500 text-white font-bold rounded-xl hover:bg-red-600 transition-all"
            >
              Change Password
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
